/**
 * <video-reels> — testimonial carousel.
 *
 * Two invariants hold this section's page weight down; see .wiki/Video-Reels.md.
 *
 * 1. The section owns exactly TWO <video> elements — a constant, never one per
 *    slide. They are rendered outside the track with no src, and this file moves
 *    the pair into whichever slide is active. One is live, the other pre-buffers
 *    the block the carousel will land on next. Slides themselves are poster
 *    images, so duplicating them for the loop costs nothing and no markup change
 *    can start N downloads at once.
 * 2. Nothing runs until the section is within 600px of the viewport — not even the
 *    Embla fetch, which this file injects rather than the layout linking. The
 *    homepage places it below the fold; page load must cost zero here.
 * 3. Video BYTES follow visibility, not approach. A buffer gets NO <source> until
 *    the section is on screen, because attaching one is the moment bytes start and
 *    `preload` is not a brake the UA honours. See arm() and .wiki/Video-Reels.md.
 */
class VideoReels extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onApproach = this.onApproach.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.sectionVisible = false;
    // Which way the carousel last moved; the prebuffer follows it. Auto-advance
    // only ever goes forward, so forward is the right thing to assume.
    this.direction = 1;
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.player = this.querySelector('[data-player]');
    // Two elements that swap roles: one plays, the other pre-buffers what is next.
    this.buffers = [...this.querySelectorAll('[data-video]')];
    this.live = 0;
    if (!this.viewport || !this.player || this.buffers.length < 2) return;

    this.addEventListener('click', this.onClick);
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    // Everything else waits until the shopper is near. rootMargin now has to buy
    // lead time for the Embla *fetch* as well as init and a poster decode, so it
    // is 600px rather than 200 — at normal scroll speed 200px is ~100ms, and the
    // carousel would visibly snap into place on a slow connection.
    this.gate = new IntersectionObserver(this.onApproach, { rootMargin: '600px' });
    this.gate.observe(this);
  }

  disconnectedCallback() {
    this.gate?.disconnect();
    this.visibility?.disconnect();
    this.embla?.destroy();
    this.embla = null;
    this.buffers?.forEach((video) => {
      video.removeEventListener('ended', this.onEnded);
      video.removeEventListener('playing', this.onPlaying);
      // Drop the sources and re-load, or a section the Theme Editor just replaced
      // leaves its prebuffer downloading into an element nothing will ever show.
      video.pause();
      video.replaceChildren();
      delete video.dataset.block;
      video.load();
    });
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  get video() {
    return this.buffers[this.live];
  }

  get spare() {
    return this.buffers[1 - this.live];
  }

  /**
   * Embla is fetched here rather than from the document head. It is 7.5 KB gzipped
   * that a shopper who never scrolls this far never needs, and `defer` still means
   * fetched, parsed and executed before DOMContentLoaded — competing for bandwidth
   * with the above-the-fold banner on the one section that guarantees it needs
   * nothing until scroll.
   *
   * The gate stays connected until the fetch succeeds. With two deferred scripts
   * the load could not fail independently; a lazy fetch can, and disconnecting
   * first would strand the section in its no-JS scroll-snap state for the life of
   * the page. On failure a later crossing tries again.
   */
  async onApproach(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    if (this.loadingEmbla) return;
    this.loadingEmbla = true;
    try {
      await this.loadEmbla();
    } catch {
      this.loadingEmbla = false;
      return;
    }
    this.gate.disconnect();
    this.init();
  }

  /** Inject the vendored UMD bundle once, and hand every caller the same promise. */
  loadEmbla() {
    if (typeof EmblaCarousel !== 'undefined') return Promise.resolve();
    if (!this.dataset.emblaSrc) return Promise.reject(new Error('video-reels: no data-embla-src'));
    this.emblaRequest ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = this.dataset.emblaSrc;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener(
        'error',
        () => {
          script.remove();
          reject(new Error('video-reels: embla failed to load'));
        },
        { once: true },
      );
      document.head.appendChild(script);
    }).catch((error) => {
      // Drop the rejected promise so a retry is a fresh request, not a cached no.
      this.emblaRequest = null;
      throw error;
    });
    return this.emblaRequest;
  }

  init() {
    if (this.embla || typeof EmblaCarousel === 'undefined') return;

    // Source URLs live only on the real slide set; duplicates carry data-index
    // alone, which is what keeps them inert markup rather than media.
    this.sources = [];
    this.querySelectorAll('.video-reels__slide:not([data-duplicate])').forEach((slide) => {
      this.sources[Number(slide.dataset.index)] = {
        hls: slide.dataset.hls,
        mp4: slide.dataset.mp4,
        label: slide.dataset.videoLabel || '',
      };
    });
    this.blockCount = this.sources.length;

    // Duplicates ship hidden so a no-JS visitor sees each testimonial once.
    this.querySelectorAll('[data-duplicate]').forEach((slide) => slide.removeAttribute('hidden'));

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoAdvance = this.dataset.autoAdvance === 'true' && !reducedMotion;

    this.embla = EmblaCarousel(this.viewport, {
      loop: true,
      align: 'center',
      containScroll: false,
      duration: reducedMotion ? 0 : 20,
    });

    this.embla.on('select', this.onSelect);
    this.buffers.forEach((video) => {
      video.addEventListener('ended', this.onEnded);
      video.addEventListener('playing', this.onPlaying);
    });

    this.classList.add('is-ready');
    this.onSelect();

    // Embla drops to loop:false in silence when the strip cannot wrap. Say so —
    // a carousel that dead-ends on a wide monitor is otherwise very hard to trace
    // back to the copies budget in video-reels.liquid.
    const strip = this.embla.containerNode().getBoundingClientRect?.().width ?? 0;
    if (strip && strip < this.viewport.clientWidth * 1.5) {
      console.warn(
        `[video-reels] ${Math.round(strip)}px strip in a ${this.viewport.clientWidth}px viewport ` +
          'is too narrow to loop — raise the pixel budget in video-reels.liquid',
      );
    }

    this.visibility = new IntersectionObserver(this.onVisibility, { threshold: 0.1 });
    this.visibility.observe(this);
  }

  get activeSlide() {
    return this.embla?.slideNodes()[this.embla.selectedScrollSnap()];
  }

  /**
   * Move the player into the active slide and put that block on screen.
   *
   * If the spare buffer already holds this block — the usual case, because the
   * previous select warmed it — the two swap roles and playback starts from an
   * already-buffered element instead of a cold `load()`.
   *
   * Order matters: removing a media element from the document obliges the UA to
   * pause it, so playback is stopped before the move and only started after.
   */
  onSelect() {
    const slide = this.activeSlide;
    if (!slide) return;

    const index = Number(slide.dataset.index);
    this.slideNodes ||= this.embla.slideNodes();
    this.slideNodes.forEach((node) => {
      const isActive = node === slide;
      node.classList.toggle('active', isActive);
      // Duplicates ship aria-hidden; the one currently on screen must not be, or
      // the slide the shopper is looking at is the one a reader cannot reach.
      if (node.dataset.duplicate === undefined) return;
      if (isActive) node.removeAttribute('aria-hidden');
      else node.setAttribute('aria-hidden', 'true');
    });
    this.updateDots(index);
    this.trackDirection(index);

    const source = this.sources[index];
    const media = slide.querySelector('.video-reels__media');
    if (!source || !media || (!source.hls && !source.mp4)) {
      // `hidden` is display:none, which does NOT pause a media element — without
      // this the block we just left keeps playing, audible, behind a blank slide.
      this.buffers.forEach((video) => video.pause());
      this.player.hidden = true;
      return;
    }

    // Promote the warmed buffer if it is already holding this block.
    if (this.spare.dataset.block === String(index)) this.live = 1 - this.live;

    this.buffers.forEach((video) => video.pause());
    // Only move when the player is somewhere else: re-parenting a media element
    // detaches it, which costs a pause and a repaint of the video surface.
    if (this.player.parentElement !== media) media.appendChild(this.player);
    this.player.hidden = false;

    // No source at all until the section is on screen — see arm(). Everything
    // below still runs, so the element is labelled and configured and ready for
    // onVisibility to re-enter here and arm it.
    if (this.sectionVisible) this.arm(this.video, index, source);
    this.video.setAttribute('aria-label', source.label);
    // Loop in place only when the carousel will not advance for us.
    this.video.loop = !this.autoAdvance;

    this.buffers.forEach((video) => {
      const live = video === this.video;
      video.muted = this.dataset.sound !== 'on';
      video.classList.toggle('is-live', live);
      // The spare is a buffer, not content: keep it out of the a11y tree so the
      // slide exposes one labelled video rather than two, one of them nameless.
      // Must be the string "true" — aria-hidden="" reads as absent, not as hidden.
      if (live) video.removeAttribute('aria-hidden');
      else video.setAttribute('aria-hidden', 'true');
    });

    if (this.sectionVisible) this.play();
  }

  /**
   * Remember which way the carousel is moving so the prebuffer follows the
   * shopper instead of always guessing forward. A dot jump matches neither
   * neighbour, so it keeps whatever direction was already in play.
   */
  trackDirection(index) {
    const previous = this.lastIndex;
    this.lastIndex = index;
    if (previous === undefined || !this.blockCount) return;
    if ((previous + 1) % this.blockCount === index) this.direction = 1;
    else if ((previous - 1 + this.blockCount) % this.blockCount === index) this.direction = -1;
  }

  /**
   * Point a buffer at a block, skipping the reload if it already holds it.
   *
   * ATTACHING A SOURCE IS THE MOMENT BYTES START, which is why nothing calls this
   * until the section is on screen.
   *
   * `preload` is not a brake. An earlier fix armed on approach at
   * `preload="metadata"`, on the reasonable theory that it would fetch manifests
   * and stop. Chrome buffers the segments anyway: measured 2026-08-17 on a PDP
   * reloaded with the reel below the fold and no scrolling at all — four .ts
   * segments, ~1.8 MB, on a section the shopper never reached. `metadata` did move
   * ABR down to the SD-480p rung from the HD-1080p one it takes at `auto`, which is
   * why that number is 1.8 MB rather than the 5.2 MB originally measured. A smaller
   * leak is still a leak.
   *
   * An element with no `<source>` cannot download anything, whatever the UA makes
   * of `preload`. That is the only guarantee on offer, so it is the one used.
   */
  arm(video, index, source) {
    if (video.dataset.block === String(index)) return;
    video.dataset.block = String(index);
    video.classList.remove('is-painted');
    video.preload = 'auto';
    video.replaceChildren();
    this.attachSource(video, source);
    video.load();
  }

  /**
   * Give the element exactly ONE <source>, chosen here rather than left to the
   * UA's source-selection walk.
   *
   * This is not a bandwidth fix and should not be sold as one — a browser picks a
   * single source and never fetches the others, so the mp4 was already inert on
   * anything with native HLS. What it fixes is intent: the storefront only ever
   * exposes the TOP mp4 rung (see .wiki/Shopify-CDN-Media.md), so that element was
   * an unreachable 7.2 Mbps file sitting in the markup of every armed buffer,
   * which reads as a deliberate fallback ladder and is not one.
   *
   * A false negative from canPlayType is safe: it emits the mp4, which is exactly
   * what a browser without native HLS needed anyway.
   */
  attachSource(video, source) {
    if (source.hls && this.supportsHls) {
      video.appendChild(this.buildSource(source.hls, 'application/x-mpegURL'));
      return;
    }
    if (source.mp4) {
      video.appendChild(this.buildSource(source.mp4, 'video/mp4'));
      return;
    }
    // No mp4 rung on this video — hand the UA the playlist and let it try.
    if (source.hls) video.appendChild(this.buildSource(source.hls, 'application/x-mpegURL'));
  }

  /** Native HLS: Safari/iOS, Chrome Android, Chromium 142+. Probed once. */
  get supportsHls() {
    if (this.hlsSupport === undefined) {
      const probe = document.createElement('video');
      this.hlsSupport = ['application/vnd.apple.mpegurl', 'application/x-mpegURL'].some(
        (type) => probe.canPlayType(type) !== '',
      );
    }
    return this.hlsSupport;
  }

  /**
   * Buffer the block the carousel will land on next, on the element that is not
   * playing. This is the whole point of having two: by the time the shopper gets
   * there the segments are in cache, so the swap has no visible stall.
   *
   * Called from `playing`, never from `onSelect` — starting the next download
   * alongside the current one would have the two competing for bandwidth and
   * make the video the shopper is actually waiting on start *later*.
   *
   * Skipped entirely when the shopper is paying for the bytes. Prefetching a
   * whole second video is a bet that they will keep scrolling; on Data Saver or
   * a 2g connection that bet is not ours to make, and losing it costs them real
   * money. Safari exposes no navigator.connection, so it always prefetches.
   */
  warmNext() {
    if (!this.blockCount || this.blockCount < 2) return;
    if (this.constrainedConnection) return;
    const current = Number(this.video.dataset.block);
    if (Number.isNaN(current)) return;
    const nextIndex = (current + this.direction + this.blockCount) % this.blockCount;
    const next = this.sources[nextIndex];
    if (!next || (!next.hls && !next.mp4)) return;
    this.arm(this.spare, nextIndex, next);
  }

  get constrainedConnection() {
    const connection = navigator.connection;
    if (!connection) return false;
    return connection.saveData === true || ['slow-2g', '2g'].includes(connection.effectiveType);
  }

  buildSource(src, type) {
    const node = document.createElement('source');
    node.src = src;
    node.type = type;
    return node;
  }

  play() {
    // Dawn's pauseAllMedia() sweeps every <video> on the page — ours included — so
    // it pauses the element we are about to play and stalls the spare mid-prebuffer.
    // Run it once, on the first play, when the spare has no source to lose. After
    // that the only media still playing on the page is this section's own.
    if (!this.stoppedOtherMedia) {
      this.stoppedOtherMedia = true;
      window.pauseAllMedia?.();
    }
    this.video.play().catch(() => {});
  }

  /**
   * A frame is on screen only when its element is both live and painting, and
   * both flags live on the element itself. An earlier version put the state on
   * the slide's media box and cleared it only for the incoming slide, so the
   * slide the player had just left kept a permanently transparent poster.
   */
  onPlaying(event) {
    event.target.classList.add('is-painted');
    // The live video is up; the connection is now free to warm the next one.
    if (event.target === this.video) this.warmNext();
  }

  // Both buffers are listened to; only the live one advancing should count.
  onEnded(event) {
    if (event.target !== this.video) return;
    if (this.autoAdvance && this.sectionVisible) this.embla?.scrollNext();
  }

  onVisibility(entries) {
    entries.forEach((entry) => {
      this.sectionVisible = entry.isIntersecting;
      if (this.player.hidden) return;
      if (entry.isIntersecting) {
        // First time on screen the live buffer has no source yet, because
        // onSelect() refused to attach one off screen. Re-enter onSelect: it is
        // idempotent, and it both arms and plays now that sectionVisible is true.
        // On a later return the source is still attached — coming back must not
        // re-download — so it is only a play().
        if (this.video.dataset.block === undefined) this.onSelect();
        else this.play();
      } else {
        this.buffers.forEach((video) => video.pause());
      }
    });
  }

  // Dots are server-rendered one per block; the active one is the active slide's
  // position within its duplicate set. `current` is passed in — recomputing it
  // from activeSlide would re-walk Embla's slide list on every select.
  updateDots(current) {
    if (!this.blockCount) return;
    this.dotNodes ||= this.querySelectorAll('[data-slide-index]');
    this.dotNodes.forEach((dot, index) => {
      dot.classList.toggle('video-reels__dot--active', index === current);
      if (index === current) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  // Jump to the copy of `index` nearest the current position, so a dot click
  // never sweeps the whole strip.
  goToBlock(index) {
    if (!this.embla) return;
    this.slideNodes ||= this.embla.slideNodes();
    const current = this.embla.selectedScrollSnap();
    const total = this.slideNodes.length;
    let best = index;
    let bestDistance = Infinity;
    for (let candidate = index; candidate < total; candidate += this.blockCount) {
      const distance = Math.abs(candidate - current);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }
    this.embla.scrollTo(best);
  }

  onClick(event) {
    if (event.target.closest('[data-sound-toggle]')) {
      this.toggleSound();
      return;
    }
    if (!this.embla) return;
    if (event.target.closest('[data-nav-prev]')) {
      this.embla.scrollPrev();
      return;
    }
    if (event.target.closest('[data-nav-next]')) {
      this.embla.scrollNext();
      return;
    }
    const dot = event.target.closest('[data-slide-index]');
    if (dot) {
      this.goToBlock(Number(dot.dataset.slideIndex));
      return;
    }
    const slide = event.target.closest('.video-reels__slide');
    if (!slide) return;
    if (slide.classList.contains('active')) {
      // Clicking the playing video toggles sound — it never pauses playback.
      if (event.target.closest('video')) this.toggleSound();
    } else {
      this.goToBlock(Number(slide.dataset.index));
    }
  }

  toggleSound() {
    const soundOn = this.dataset.sound !== 'on';
    this.dataset.sound = soundOn ? 'on' : 'off';
    this.buffers.forEach((video) => {
      video.muted = !soundOn;
    });
    const button = this.querySelector('[data-sound-toggle]');
    button?.setAttribute('aria-label', soundOn ? button.dataset.labelMute : button.dataset.labelUnmute);
  }

  async onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    // The editor selects a real slide; init may not have run if it is off screen,
    // in which case Embla is not fetched yet either.
    try {
      await this.loadEmbla();
    } catch {
      return;
    }
    this.gate?.disconnect();
    this.init();
    const slide = event.target.closest('.video-reels__slide');
    if (slide) this.goToBlock(Number(slide.dataset.index));
  }
}

customElements.get('video-reels') || customElements.define('video-reels', VideoReels);
